import React, { useState } from "react";
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import AccordionDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InputField, { SIZES } from "../_shared/input-field/InputField";
import "./PreLogin.scss";
import { Typography, Button, CircularProgress } from "@material-ui/core";
import WelcomeHeader from "../_shared/welcome-header/WelcomeHeader";
import SSO from "../../sso";
import config from "../../sso/config";
import _config from "../../config";
import InputLabel from "@material-ui/core/InputLabel";
import PropTypes from "prop-types";

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  SERVER_ERROR: 500,
};

const renderTextField = ({ input, ...custom }) => (
  <InputField {...input} {...custom} />
);

renderTextField.defaultProps = {
  input: null,
};

renderTextField.propTypes = {
  input: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
};

/**
 * PreLogin Component
 * Landing page for user authentication - supports both enterprise and external users
 */
const PreLogin = () => {
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [isError, setError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Initiates login flow for internal/enterprise users
   * Redirects to OIDC authentication with JWT enabled
   */
  const initateLoginForWalmartUsers = () => {
    localStorage.setItem(config.fetch_details_param, false);
    window.location.href = `${_config.BASE_URL}login?jwt=true`;
  };

  /**
   * Validates and initiates login for external users
   */
  const initateLoginForExternalUsers = () => {
    // Validate inputs
    if (!username || username.trim() === '') {
      setError(true);
      setErrMsg('User Id required');
      return;
    }
    
    if (!password || password.trim() === '') {
      setError(true);
      setErrMsg('Password required');
      return;
    }
    
    setIsLoading(true);
    localStorage.setItem(config.fetch_details_param, true);
    
    SSO.signInExternalUsers(username, password)
      .then((res) => {
        // ← NEW: Handle 2FA requirement (stateless challenge token model)
        if (res && res.status === 'TWOFA_REQUIRED') {
          /* --- OTP Required ---
           * User passed password authentication but needs to verify OTP.
           * Challenge token is stored in sessionStorage by utility.
           * Redirect to OTP entry page.
           */
          window.location.href = '/twoFactorOTP';
          return;  // Exit without setting isLoading=false (page redirecting)
        }

        // ← EXISTING: Handle errors
        if (res && (res.status === HTTP_STATUS.UNAUTHORIZED || res.status === HTTP_STATUS.SERVER_ERROR)) {
          setError(true);
          setErrMsg(res.error || 'Login failed. Please try again.');
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('External user login error:', err);
        setError(true);
        setErrMsg('An unexpected error occurred. Please try again.');
        setIsLoading(false);
      });
  };

  const handleInputChange = (setter) => (event) => {
    setErrMsg('');
    setError(false);
    setter(event.target.value);
  };

  return (
    <div className="pre-login">
      <WelcomeHeader
        title="Welcome to eClosing!"
        subtitle="Sign into eClosing with"
      />
      {isLoading && <CircularProgress size={30} />}
      
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} id="panel1a-header">
          <Typography>Enterprise Login</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Button
            color="primary"
            variant="contained"
            className="pre-login-buttons"
            type="button"
            onClick={initateLoginForWalmartUsers}
            disabled={isLoading}
          >
            Walmart Inc
          </Button>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} id="panel2a-header">
          <Typography>External User</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <form className="form-container" autoComplete="off">
            {isError && errMsg && (
              <div className="error-msg">
                <Typography className="msg">{errMsg}</Typography>
              </div>
            )}
            
            <InputField
              name="username"
              inputLabel="Username"
              inputType="text"
              placeholder="Username"
              inputStyleType={SIZES.LARGE}
              showInputAdornment={false}
              onChange={handleInputChange(setUserName)}
            />
            
            <InputField
              name="password"
              inputLabel="Password"
              inputType="password"
              placeholder="Password"
              showInputAdornment
              inputStyleType={SIZES.LARGE}
              onChange={handleInputChange(setPassword)}
            />

            <Button
              color="primary"
              variant="contained"
              className="pre-login-buttons"
              type="button"
              onClick={initateLoginForExternalUsers}
              disabled={isLoading}
            >
              Sign In
            </Button>
          </form>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default PreLogin;
